import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";
import WeeklyScheduleTable, { toWeek, weekHasErrors, weekIsRestricted } from "../WeeklyScheduleTable";
import MessageField from "../SatisfactionSurveyModal/MessageField";

const t = (key, options) => i18n.t(`settings.attention.hours.${key}`, options);

const QueueHoursDialog = ({ queue, onClose, onSaved }) => {
  const [week, setWeek] = useState(() => toWeek(queue?.schedules));
  const [message, setMessage] = useState(queue?.outOfHoursMessage || "");
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setWeek(toWeek(queue?.schedules));
    setMessage(queue?.outOfHoursMessage || "");
    setShowErrors(false);
  }, [queue]);

  const messageMissing = weekIsRestricted(week) && !message.trim();

  const handleSave = async () => {
    if (weekHasErrors(week) || messageMissing) {
      setShowErrors(true);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/business-hours/queue/${queue.id}`, {
        schedules: week,
        outOfHoursMessage: message.trim(),
      });
      toast.success(t("saved"));
      onSaved(data);
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  return (
    <Dialog open={Boolean(queue)} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>{t("queueDialogTitle", { name: queue?.name || "" })}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2}>
          <WeeklyScheduleTable value={week} onChange={setWeek} />
          {showErrors && weekHasErrors(week) && <Alert severity="error">{t("fixErrors")}</Alert>}
          <MessageField
            label={t("outOfHoursMessage")}
            value={message}
            onChange={setMessage}
            required={weekIsRestricted(week)}
            error={showErrors && messageMissing}
            helperText={showErrors && messageMissing ? t("messageRequired") : t("outOfHoursHelp")}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("cancel")}
        </Button>
        <Box position="relative">
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {t("save")}
          </Button>
          {saving && (
            <CircularProgress size={22} sx={{ position: "absolute", top: "50%", left: "50%", mt: "-11px", ml: "-11px" }} />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default QueueHoursDialog;
